import * as THREE from "three";
import TWEEN from "@tweenjs/tween.js";
import { OrbitControls } from "./controls/OrbitControls";
import { Sky, Water } from "three-stdlib";
import Boat from "./Boat";

export default class SceneOcean extends THREE.Scene {
  constructor(player, config, repeat = false) {
    super();

    this.player = player;
    this.config = config;
    this.hotspots = [];
    this.boatScale = 1;
    const renderTargetParams = {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBFormat,
      stencilBuffer: false,
    };
    /**
     * FBO for this scene
     */
    this.fbo = new THREE.WebGLRenderTarget(
      window.innerWidth,
      window.innerHeight,
      renderTargetParams,
    );

    this.boat = new Boat(config, () => this.finishedLoad(), this);

    // Add sky
    this.sky = new Sky();
    this.sky.scale.setScalar(10000);

    // Add ambient light to stop the 'dark side of the boat'
    this.ambLight = new THREE.AmbientLight(0xffffff, 0.2);

    this.loadingCallback = player.loadingManager.start(!repeat);
    this.loadCount = 2;

    // Create directional light
    // This becomes the sun
    this.light = new THREE.DirectionalLight(0xffffff, 0.8);
    this.light.castShadow = true;
    this.light.shadow.bias = -0.002;
    this.light.position.set(0, 100, 0);
    this.light.shadow.camera.top = 40;
    this.light.shadow.camera.bottom = -40;
    this.light.shadow.camera.left = 40;
    this.light.shadow.camera.right = -40;

    // TODO needs moving
    player.renderer.autoClear = false;
    player.renderer.shadowMap.enabled = true;
    player.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    player.controlsOrbit.enabled = false;

    // Setup sky uniforms
    const uniforms = this.sky.material.uniforms;
    uniforms.turbidity.value = 10;
    uniforms.rayleigh.value = 2;
    uniforms.mieCoefficient.value = 0.005;
    uniforms.mieDirectionalG.value = 0.8;

    // Position of the sun
    this.skyParams = {
      distance: 400,
      inclination: 0.15,
      azimuth: 0.205,
    };

    // Create the water plane
    const waterGeom = new THREE.PlaneBufferGeometry(10000, 10000);
    this.water = new Water(waterGeom, {
      textureWidth: 512,
      textureHeight: 512,
      waterNormals: new THREE.TextureLoader().load(
        `${window.location.origin}/tour/assets/waternormals.jpg`,
        texture => {
          texture.wrapS = texture.wrapT = THREE.RepeatWrapping;

          this.finishedLoad();
        },
      ),
      alpha: 1.0,
      sunDirection: this.light.position.clone().normalize(),
      sunColor: 0xffffff,
      waterColor: 0x049db1,
      // waterColor: 0x001e0f,
      distortionScale: 3.7,
    });

    this.water.rotation.x = -Math.PI / 2;
    this.water.receiveShadow = true;

    // This allows you to see some of the ship below water
    this.water.material.uniforms.alpha.value = 0.95;

    // Set initial sun position
    this.updateSky();

    // Create camera that is only used in this scene
    this.camera = new THREE.PerspectiveCamera(
      55,
      window.innerWidth / window.innerHeight,
      1,
      20000,
    );
    const initialPosition = { x: 250, y: 120, z: -90 };

    this.camera.position.set(
      initialPosition.x,
      initialPosition.y,
      initialPosition.z,
    );
    this.camera.lookAt(0, 10, 0);

    // As we use a different camera, we need to handle resize here too
    window.addEventListener("resize", this.onWindowResize.bind(this), false);

    this.controls = new OrbitControls(this.camera, player.renderer.domElement);
    this.controls.mainCamera = this.camera;
    this.controls.maxPolarAngle = Math.PI * 0.495;
    this.controls.target.set(0, 10, 0);
    this.controls.minDistance = 0;
    this.controls.maxDistance = 200;
    this.controls.enablePan = false;
    this.controls.enableDamping = true;
    // this.controls.dampingFactor = 0.01;
    this.controls.rotateSpeed = 1.0;
    this.camera.lookAt(this.controls.target);
  }

  /**
   * This is triggered when window is resized.
   * It's used to change the camera aspect.
   * We already update the renderer in Player
   */
  onWindowResize() {
    const width = this.player.container.clientWidth;
    const height = this.player.container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.updateBoat();
  }

  updateBoat() {
    if (this.boat) {
      const width = this.player.container.clientWidth;
      const height = this.player.container.clientHeight;
      const ratio = width / height;

      const defaultScale = 32 / this.boat.maxPosition;
      const scale = Math.min(defaultScale * 1.5, defaultScale * ratio);

      this.boatScale = scale;

      this.boat.scale.setScalar(scale);
      // this.boat.position.y = -2.2 * scale;
    }
  }

  /**
   * Update sky updates the light position based on sky params
   */
  updateSky() {
    const theta = Math.PI * (this.skyParams.inclination - 0.5);
    const phi = 2 * Math.PI * (this.skyParams.azimuth - 0.5);

    this.light.position.x = this.skyParams.distance * Math.cos(phi);
    this.light.position.y =
      this.skyParams.distance * Math.sin(phi) * Math.sin(theta);
    this.light.position.z =
      this.skyParams.distance * Math.sin(phi) * Math.cos(theta);

    this.sky.material.uniforms.sunPosition.value = this.light.position.copy(
      this.light.position,
    );
    this.water.material.uniforms.sunDirection.value
      .copy(this.light.position)
      .normalize();
  }

  /**
   * When an asset load is finished, call this.
   * This will hide the loading container
   */
  finishedLoad() {
    this.loadCount -= 1;

    if (this.loadCount === 0) {
      this.updateBoat();
      this.start();
      this.loadingCallback();
    }
  }

  /**
   * Called every frame before render
   */
  update() {
    this.boat.update();
  }

  /**
   * Simply renders the scene.
   * Updates the water time to allow for animations
   */
  render(renderer, camera, useFbo) {
    this.water.material.uniforms.time.value += 1.0 / 60.0;
    // this.controls.update();

    if (useFbo) {
      renderer.render(this, this.camera, this.fbo, true);
    } else {
      renderer.render(this, this.camera);
    }
  }

  /**
   * Start is called when load is complete and it's time to begin!
   */
  start() {
    // Add everything to the scene
    this.add(this.boat);
    this.add(this.sky);
    this.add(this.ambLight);
    this.add(this.light);
    this.add(this.water);

    const finalPosition = {
      x: 0,
      y: 55,
      z: -80,
    };

    setTimeout(() => this.toggleBoatOpacity(), 3500);

    const tween = new TWEEN.Tween(this.camera.position).to(finalPosition, 3000);
    tween.onUpdate(() => {
      this.camera.lookAt(0, 10, 0);
    });
    tween.easing(TWEEN.Easing.Quintic.InOut);
    tween.start();
  }

  /**
   * Called just before deleting.
   * Used to clean up!
   */
  destroy() {
    window.removeEventListener("resize", this.onWindowResize.bind(this), false);
  }

  onMouseUp(event, mouse, camera) {
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.camera);

    // Check if any hotspots are hit
    const intersects = raycaster.intersectObjects(this.hotspots, true);

    if (intersects.length > 0) {
      const intersect = intersects[0];
      const object = intersects[0].object;
      const image = object.parent.getImage();
      intersect.position = intersect.point;

      const start = new THREE.Euler().copy(this.camera.rotation);
      this.camera.lookAt(intersect.point);
      const end = new THREE.Euler().copy(this.camera.rotation);
      this.camera.rotation.copy(start);

      if (Math.abs(start.z - end.z) > Math.PI) {
        if (start.z < end.z) {
          end.z -= Math.PI * 2;
        } else {
          end.z += Math.PI * 2;
        }
      }

      const scale = { value: 1 };
      const hotspotTween = new TWEEN.Tween(scale).to({ value: 0 }, 500);
      hotspotTween.onUpdate(() => {
        for (const hotspot of this.hotspots) {
          hotspot.setScale(scale.value);
        }
      });
      hotspotTween.easing(TWEEN.Easing.Quartic.In);
      hotspotTween.start();

      setTimeout(() => {
        this.target = object.position;

        // This is used because the boat is currently flipped.
        this.target.x *= -1;

        // How much before or after the camera position should be
        let offset = 0.25;

        if (this.target.x >= 0) {
          offset *= -1;
        }

        // Final position for the camera
        const finalPosition = {
          x: (this.target.x + offset) * this.boatScale,
          y: (this.target.y + 0.1) * this.boatScale,
          z: this.target.z * this.boatScale,
        };

        // Camera starts at looking towards the center
        const lookAt = {
          x: 0,
          y: 10,
          z: 0,
        };

        // needs to fade
        setTimeout(() => this.player.transitionTo(image, intersect), 2000);

        // Tween the position of the camera towards the final position
        const tween = new TWEEN.Tween(this.camera.position).to(
          finalPosition,
          3500,
        );
        tween.onUpdate(() => {
          // This tween ends after the lookAt tween, so much keep looking at.
          this.camera.lookAt(lookAt.x, lookAt.y, lookAt.z);
        });
        tween.easing(TWEEN.Easing.Quartic.InOut);
        tween.start();

        // Location of the node
        const targetAt = {
          x: this.target.x * this.boatScale,
          y: this.target.y * this.boatScale,
          z: this.target.z * this.boatScale,
        };

        // Tweens the look at value for move camera movement
        const rTween = new TWEEN.Tween(lookAt).to(targetAt, 2500);
        rTween.onUpdate(() => {
          this.camera.lookAt(lookAt.x, lookAt.y, lookAt.z);
        });
        rTween.easing(TWEEN.Easing.Quartic.InOut);
        rTween.start();
      }, 250);
    }
  }

  /**
   * Not currently used
   */
  startTransition() {}

  onMouseMove(event, mouse, camera) {
    // for (const hotspot of this.hotspots) {
    //   if (hotspot.getOpacity() < 0.4) {
    //     return;
    //   }
    //   hotspot.setOpacity(1);
    // }
    // const raycaster = new THREE.Raycaster();
    // raycaster.setFromCamera(mouse, this.camera);
    // // Check if any hotspots are hit
    // const intersects = raycaster.intersectObjects(this.hotspots, true);
    // if (intersects.length > 0) {
    //   for (const intersect of intersects) {
    //     intersect.object.parent.setOpacity(0.7);
    //   }
    // }
  }

  static injectExample(tour) {
    tour.data.intro = {
      type: "ocean",
      model: `${window.location.origin}/tour/assets/tamsen.obj`,
      materials: `${window.location.origin}/tour/assets/tamsen.mtl`,
      decks: [
        {
          model: `${window.location.origin}/tour/assets/tamsen-deck.obj`,
          materials: `${window.location.origin}/tour/assets/tamsen-deck.mtl`,
          hotspots: [
            { x: -1.1067, y: 0.50503, z: 0, image: 2363 },
            { x: 1.2367, y: 0.50503, z: 0, image: 2377 },
            { x: -0.5067, y: 0.30503, z: 0, image: 2391 },
          ],
        },
      ],
    };
  }

  toggleBoatOpacity() {
    if (this.boatFaded === true) {
      const opacity = { value: 0.3 };
      const boatTween = new TWEEN.Tween(opacity).to({ value: 1 }, 500);
      boatTween.onUpdate(() => {
        this.boat.setOpacity(opacity.value);
      });
      boatTween.easing(TWEEN.Easing.Quadratic.InOut);
      boatTween.start();

      const scale = { value: 1 };
      const hotspotTween = new TWEEN.Tween(scale).to({ value: 0 }, 500);
      hotspotTween.onUpdate(() => {
        for (const hotspot of this.hotspots) {
          hotspot.setScale(scale.value);
        }
      });
      hotspotTween.easing(TWEEN.Easing.Cubic.InOut);
      hotspotTween.start();

      this.boatFaded = false;
    } else {
      const opacity = { value: 1 };
      const boatTween = new TWEEN.Tween(opacity).to({ value: 0.3 }, 500);
      boatTween.onUpdate(() => {
        this.boat.setOpacity(opacity.value);
      });
      boatTween.easing(TWEEN.Easing.Quadratic.InOut);
      boatTween.start();

      const scale = { value: 0 };
      const hotspotTween = new TWEEN.Tween(scale).to({ value: 1 }, 500);
      hotspotTween.onUpdate(() => {
        for (const hotspot of this.hotspots) {
          hotspot.setScale(scale.value);
        }
      });
      hotspotTween.easing(TWEEN.Easing.Cubic.InOut);
      hotspotTween.start();

      this.boatFaded = true;
    }
  }
}
